﻿import { useState } from "react";
import { ReservationPosItem, ReservationPosItemDto, ReservationState } from './types';
import { format } from "date-fns";
import { tryParseFloat, tryParseInt } from '@/utils/utils';
import { getRevenueLockDateInputMin, pollForChangeRequest, refreshDiffs, reloadPosItems } from './utils';
import { useClientSettings } from './ClientSettingsContext';

export function EditPosItemDialogComponent({ reservationState, posItem, close }: EditPosItemDialogComponentProps) {

    const { enablePackagePriceOverride } = useClientSettings();
    const canChangeAmount = posItem.CanChangeAmount && (!posItem.IsPackage || enablePackagePriceOverride);

    const [overrideEffectiveDate, setOverrideEffectiveDate] = useState(false);
    const [effectiveDate, setEffectiveDate] = useState(format(new Date(), 'yyyy-MM-dd'));

    function effectiveDateChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setEffectiveDate(e.target.value);
    }

    const [adjustQuantity, setAdjustQuantity] = useState(posItem.Quantity);
    const [isAdjustQuantityFocused, setIsAdjustQuantityFocused] = useState(false);

    function adjustQuantityChanged(e: React.ChangeEvent<HTMLInputElement>) {
        let value = tryParseInt(e.target.value, adjustQuantity);
        setAdjustQuantity(value);
    }

    const [adjustAmount, setAdjustAmount] = useState(posItem.Amount);
    const [isAdjustAmountFocused, setIsAdjustAmountFocused] = useState(false);

    function adjustAmountChanged(e: React.ChangeEvent<HTMLInputElement>) {
        let value = tryParseFloat(e.target.value, adjustAmount);
        setAdjustAmount(value);
    }

    const [adjustmentNote, setAdjustmentNote] = useState('');

    function adjustmentNoteChanged(e: React.ChangeEvent<HTMLTextAreaElement>) {
        setAdjustmentNote(e.target.value);
    }

    const [adjustingPosItem, setAdjustingPosItem] = useState(false);

    async function adjustPosItem() {
        setAdjustingPosItem(true);

        const request: AdjustPosItemRequest = {
            //effectiveDate: effectiveDate,
            quantity: adjustQuantity,
            amount: adjustAmount,
            note: adjustmentNote
        };

        const response = await fetch(
            `/api/sales/reservations/${reservationState.ReservationId}/orders/${posItem.ReservationOrderId}/items/${posItem.ReservationOrderLineItemId}/adjustments`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(request)
            });

        const responseData: AdjustPosItemResponse = await response.json();

        await pollForChangeRequest(reservationState.ReservationId, responseData.changeRequestId);

        await reloadPosItems(reservationState);

        setAdjustingPosItem(false);

        close?.();
    }


    async function removePosItem() {
        setAdjustingPosItem(true);

        const request: RemovePosItemRequest = {
            effectiveDate: overrideEffectiveDate ? effectiveDate : null
        };

        const response = await fetch(
            `/api/sales/reservations/${reservationState.ReservationId}/orders/${posItem.ReservationOrderId}/items/${posItem.ReservationOrderLineItemId}`,
            {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(request)
            });

        const responseData: AdjustPosItemResponse = await response.json();

        await pollForChangeRequest(reservationState.ReservationId, responseData.changeRequestId);

        await reloadPosItems(reservationState);

        setAdjustingPosItem(false);

        close?.();
    }

    const isValid = effectiveDate.length > 0 && adjustAmount >= 0;

    return (

        <div className="flex flex-col gap-2">
            <div>
                <table className="table">
                    <tbody>
                        <tr>
                            <th>Item</th>
                            <td className="px-2 text-right" colSpan={2}>{posItem.Description}</td>
                        </tr>
                        <tr>
                            <th>Quantity</th>
                            {posItem.CanRemove && (
                                <td className="px-2 text-right" colSpan={2}>
                                    <div className="flex flex-row place-content-end items-center gap-2 text-right">
                                        <input disabled={adjustingPosItem} className="form-control w-32 text-right" type="number" min="1" step="1"
                                            value={isAdjustQuantityFocused ? adjustQuantity : adjustQuantity}
                                            onChange={adjustQuantityChanged}
                                            onFocus={() => setIsAdjustQuantityFocused(true)}
                                            onBlur={() => {
                                                setIsAdjustQuantityFocused(false);
                                                setAdjustQuantity(Math.round(adjustQuantity));
                                            }} />
                                    </div>
                                </td>
                            )}
                            {!posItem.CanRemove && (
                                <td className="px-2 text-right" colSpan={2}>{posItem.Quantity}</td>
                            )}
                        </tr>
                        <tr>
                            <th>Amount</th>
                            {canChangeAmount && (
                                <td className="px-2 text-right" colSpan={2}>
                                    <div className="flex flex-row place-content-end items-center gap-2 text-right">
                                        <input disabled={adjustingPosItem} className="form-control w-32 text-right" type="number" min="0" step="0.01"
                                            value={isAdjustAmountFocused ? adjustAmount : adjustAmount.toFixed(2)}
                                            onChange={adjustAmountChanged}
                                            onFocus={() => setIsAdjustAmountFocused(true)}
                                            onBlur={() => {
                                                setIsAdjustAmountFocused(false);
                                                setAdjustAmount(Math.round((adjustAmount * 100)) / 100);
                                            }} />
                                        <div>THB</div>
                                    </div>
                                </td>
                            )}
                            {!canChangeAmount && (
                                <td className="px-2 text-right" colSpan={2}>{posItem.Amount.toFixed(2)}</td>
                            )}
                        </tr>
                        {/*{posItem.IsRevenueLocked && (*/}
                        {/*    <tr>*/}
                        {/*        <th>Revenue Date</th>*/}
                        {/*        <td colSpan={2} className="place-items-end text-right">*/}
                        {/*            <div className="form-check">*/}
                        {/*                <input className="form-check-input" type="checkbox" value="" id="overrideEffectiveDate" checked={overrideEffectiveDate} onChange={() => setOverrideEffectiveDate(!overrideEffectiveDate)} />*/}
                        {/*                <label className="form-check-label" htmlFor="overrideEffectiveDate">*/}
                        {/*                    Override*/}
                        {/*                </label>*/}
                        {/*            </div>*/}
                        {/*            <input min={getRevenueLockDateInputMin(reservationState.RevenueLockDate)} disabled={!overrideEffectiveDate || adjustingPosItem} type="date" className="form-control w-40" value={effectiveDate} onChange={effectiveDateChanged} />*/}
                        {/*        </td>*/}
                        {/*    </tr>*/}
                        {/*)}*/}
                    </tbody>
                </table>
            </div>
            <div>
                <label>Note</label>
                <textarea disabled={adjustingPosItem} className="form-control" value={adjustmentNote} onChange={adjustmentNoteChanged}></textarea>
            </div>
            <div className="flex flex-row gap-2 place-self-end">
                <button className="btn btn-outline-contrast" onClick={adjustPosItem} disabled={adjustingPosItem || !isValid}>Update Item</button>
                {posItem.CanRemove && (
                    <button className="btn btn-outline-contrast" onClick={removePosItem} disabled={adjustingPosItem || !isValid}>Remove Item</button>
                )}
            </div>
        </div>
    );
}

export interface EditPosItemDialogComponentProps {
    reservationState: ReservationState;
    posItem: ReservationPosItem;
    close?: () => void;
}

export interface AdjustPosItemRequest {
    //effectiveDate: string;
    quantity: number;
    amount: number;
    note: string;
}

export interface RemovePosItemRequest {
    effectiveDate: string | null;
}

export interface AdjustPosItemResponse {
    changeRequestId: string;
}